import { Link } from "react-router-dom";

/**
 * Official SER Africa logo — used everywhere per brand guidelines.
 * Two variants:
 *   variant="color" (default) — full-colour, for light backgrounds
 *   variant="bw"             — grey single-colour, for use when colour is not
 *                              an option (e.g. very dark or busy backgrounds)
 *
 * Size is controlled by `height` (px). The logo's intrinsic ratio is preserved.
 * Brand guidelines forbid stretching, re-colouring, or adding effects — the
 * <img> here keeps natural aspect ratio and avoids filters.
 */
export default function BrandLogo({
  variant = "color",
  height = 56,
  linkTo = "/",
  className = "",
  // Per brand-guidelines clear-space rule of thumb: leave at least the height
  // of the leaf icon (~ logo height) of empty space around it.
  padded = false,
  // When true, multiply-blends with the background so the JPG's white
  // backdrop disappears against any light surface.
  transparent = false,
  // When true, gently darkens the grey subtext via a contrast/brightness
  // filter without distorting the brand navy or yellow-green.
  darkenGrey = false,
  ...rest
}) {
  const src =
    variant === "bw"
      ? "/brand/ser-africa-logo-bw.png"
      : "/brand/ser-africa-logo.png";

  const imgStyle = {
    height: `${height}px`,
    width: "auto",
    display: "block",
    objectFit: "contain",
  };
  if (darkenGrey) imgStyle.filter = "contrast(1.18) brightness(0.92) saturate(1.05)";

  const img = (
    <img
      src={src}
      alt="SER Africa — Society for Ecological Restoration"
      style={imgStyle}
      className={className}
      {...rest}
    />
  );

  const content = padded ? (
    <span
      style={{
        display: "inline-flex",
        padding: `${Math.round(height * 0.15)}px ${Math.round(height * 0.2)}px`,
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
      }}
    >
      {img}
    </span>
  ) : (
    img
  );

  if (linkTo) {
    return (
      <Link to={linkTo} aria-label="SER Africa home" data-testid="brand-logo">
        {content}
      </Link>
    );
  }
  return content;
}
